import { useCallback } from 'react';
import { ApolloError, QueryResult, useQuery, useMutation, FetchResult } from '@apollo/client';
import { get } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/local-storage-util';
import { Assignment } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { StoreTypename } from '../../../types';
import GET_ASSIGNMENTS from '../../queries/assignments/get-assignments-query';
import CREATE_ASSIGNMENT from '../../queries/assignments/create-assignment';
import UPDATE_ASSIGNMENT from '../../queries/assignments/update-assignment';
import DELETE_ASSIGNMENT from '../../queries/assignments/delete-assignment';
import SKIP_ASSIGNMENT from '../../queries/assignments/skip-assignment';
import UNSKIP_ASSIGNMENT from '../../queries/assignments/unskip-assignment';

import { LocalStorage } from 'types/enums';

export type AssignmentsStoreState = {
  assignments: Assignment[];
  error: ApolloError | undefined;
  isLoading: boolean;
};

export type AssignmentStoreDispatch = {
  handleCreateAssignment: (data) => Promise<Assignment>;
  handleUpdateAssignment: (data) => Promise<Assignment>;
  handleDeleteAssignment: (id: number) => void;
  handleSkipAssignment: (
    id: number,
    studentIds: number[]
  ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>> | undefined>;
  handleUnskipAssignment: (
    id: number,
    studentIds: number[]
  ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>> | undefined>;
  refetch: () => void;
};

export type AssignmentStore = [AssignmentsStoreState, AssignmentStoreDispatch];

export const getDefaultAssignmentStoreState = (): { __typename: string } => ({
  __typename: StoreTypename.ASSIGNMENT,
});

const useAssignmentStore = (): AssignmentStore => {
  const classroomId = get(LocalStorage.CLASSROOM_ID)?.[LocalStorage.CLASSROOM_ID];

  const { data, loading, error, refetch }: QueryResult<{ assignments: Assignment[] }> = useQuery(
    GET_ASSIGNMENTS,
    {
      variables: {
        where: {
          classroomId: classroomId ?? 0,
        },
      },
    }
  );

  const [createAssignment] = useMutation(CREATE_ASSIGNMENT);
  const [updateAssignment] = useMutation(UPDATE_ASSIGNMENT);
  const [deleteAssignment] = useMutation(DELETE_ASSIGNMENT);
  const [skipAssignment] = useMutation(SKIP_ASSIGNMENT);
  const [unskipAssignment] = useMutation(UNSKIP_ASSIGNMENT);

  const handleCreateAssignment = useCallback(async (data) => {
    try {
      const assignment = await createAssignment({
        variables: { data },
      });
      if (assignment.errors) throw new Error('Error in handleCreateAssignment');
      return assignment?.data?.createAssignment;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleUpdateAssignment = useCallback(async (data) => {
    try {
      const assignment = await updateAssignment({ variables: { data } });
      if (assignment.errors) throw new Error('Error in handleUpdateAssignment');
      return assignment?.data?.createAssignment;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleDeleteAssignment = useCallback(async (id: number) => {
    try {
      await deleteAssignment({ variables: { data: { id } } });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleSkipAssignment = useCallback(async (id: number, studentIds: number[]) => {
    try {
      const assignments = await skipAssignment({ variables: { data: { id, studentIds } } });
      if (assignments?.errors) throw new Error('Error in handleSkipAssignment');
      return assignments;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleUnskipAssignment = useCallback(async (id: number, studentIds: number[]) => {
    try {
      const assignments = await unskipAssignment({ variables: { data: { id, studentIds } } });
      if (assignments?.errors) throw new Error('Error in handleUnskipAssignment');
      return assignments;
    } catch (error) {
      console.log(error);
    }
  }, []);

  return [
    {
      assignments: data?.assignments || [],
      error,
      isLoading: loading,
    },
    {
      handleCreateAssignment,
      handleUpdateAssignment,
      handleDeleteAssignment,
      handleSkipAssignment,
      handleUnskipAssignment,
      refetch,
    },
  ];
};

export default useAssignmentStore;
